import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter as Router } from "react-router";
import { QueryParamProvider } from "use-query-params";
import { ReactRouter6Adapter } from "use-query-params/adapters/react-router-6";
import Routes from "routes/Routes";
import pubSub from "utils/pub-sub";
import { Provider, useSelector } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import store from "./store";
import storage from "@storage";
import { HelmetProvider } from "react-helmet-async";

import Snackbar from "shared/Snackbar";
import LoadingBar from "shared/LoadingBar";
import Splash from "shared/SplashScreen/Splash";
import Theme, { ThemeColors } from "shared/Theme";
import ErrorBoundary from "shared/ErrorBoundary";
import VersionChangedModal from "shared/VersionChangedModal";
import PasswordWarning from "shared/PasswordWarning";

import "shared/icons/library";

import { loadAllForLanguage } from "locale";
import history from "@history";

import "bootstrap/dist/css/bootstrap.css";
import "pages/Login/public-base.css";

const TaskReminderModal = lazy(() => import("shared/TaskReminderModal"));
const EventReminderModal = lazy(() => import("shared/EventReminderModal"));

const localeReady = loadAllForLanguage(storage.get("lang") || "en");

const LoggedInModals = () => {
  const isLoggedIn = useSelector(state => state?.auth?.isLoggedIn);

  if (!isLoggedIn) return null;

  return (
    <Suspense fallback={null}>
      <TaskReminderModal />
      <EventReminderModal />
    </Suspense>
  );
};

function App() {
  const [isLocaleReady, setIsLocaleReady] = useState(false);

  // when keydown pagedown/pageup in win/linux chrome, layout breaks. this function for fix it.
  const setTextareaRange = e => {
    if (e.target.type !== "textarea") return;

    if (e.key === "PageUp" || e.key === "PageDown") {
      const cursorPosition = e.key === "PageUp" ? 0 : e.target.textLength;
      e.preventDefault();
      e.target.setSelectionRange(cursorPosition, cursorPosition);
    }
  };
  const storageListener = e => {
    if (e.key === "externalClosed") {
      pubSub.trigger(e.key + e.newValue.replaceAll('"', ""));
      storage.remove("externalClosed");
    }
  };

  useEffect(() => {
    localeReady.finally(() => setIsLocaleReady(true));
  }, []);

  useEffect(() => {
    const mac = /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
    const body = document.getElementsByTagName("body")[0];

    if (!mac) {
      body.classList.add("with-scrollbar");
    }

    if (!("ontouchstart" in document.documentElement)) {
      body.classList.add("no-touch");
    }

    document.addEventListener("keydown", setTextareaRange);
    window.addEventListener("storage", storageListener);
    return () => {
      document.removeEventListener("keydown", setTextareaRange);
      window.removeEventListener("storage", storageListener);
    };
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <DndProvider backend={HTML5Backend}>
          <Theme>
            <HelmetProvider>
              <ThemeColors />
              {isLocaleReady ? (
                <>
                  <Router history={history}>
                    <QueryParamProvider adapter={ReactRouter6Adapter}>
                      <Routes />
                    </QueryParamProvider>
                    <LoggedInModals />
                    <PasswordWarning />
                  </Router>
                  <Snackbar />
                  <LoadingBar />
                </>
              ) : (
                <Splash />
              )}
            </HelmetProvider>
          </Theme>
          <VersionChangedModal isReady={isLocaleReady} />
        </DndProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
